/**
 * @jsxRuntime classic
 * @jsx jsx
 */
/** @jsxfrag */
import React, { memo, useEffect, useRef, useState } from 'react';

import { css, jsx } from '@compiled/react';
import { useIntl } from 'react-intl-next';

import Form from '@atlaskit/form';

import messages from './messages';

type Props = {
	defaultValue: string;
	onCancel?: () => void;
	onConfirm?: (newName: string) => void;
};

const TabRenameForm = ({ defaultValue, onCancel, onConfirm }: Props) => {
	const [value, setValue] = useState(defaultValue);
	const { formatMessage } = useIntl();
	const inputRef = useRef<HTMLInputElement>(null);

	useEffect(() => {
		setTimeout(() => {
			// automatically focus and select the text of the input on mount
			inputRef.current?.focus();
			inputRef.current?.select();
		}, 0);
	}, []);

	const handleSubmitAndBlur = () => {
		if (value === defaultValue || value.trim() === '') {
			onCancel?.();
			return;
		}

		onConfirm?.(value.trim());
	};

	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setValue(e.target.value);
	};

	const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.key === 'Escape') {
			e.stopPropagation();
			onCancel?.();
		}
	};

	return (
		<Form onSubmit={handleSubmitAndBlur}>
			{({ formProps }) => (
				<form {...formProps} css={formStyles}>
					<div css={inputSizerStyles} data-value={value}>
						<input
							aria-label={formatMessage(messages.tabNameInputLabel)}
							autoComplete="off"
							data-delegated-focus-ring
							css={inputStyles}
							maxLength={80}
							name="tab-name"
							onBlur={handleSubmitAndBlur}
							onChange={handleChange}
							onKeyDown={handleKeyDown}
							ref={inputRef}
							required
							/*
							 * We set the size to 1 to ensure the input will not be wider than the
							 * text it contains. Without this the input width will be the default width
							 * based on the user agent.
							 */
							size={1}
							type="text"
							value={value}
						/>
					</div>
				</form>
			)}
		</Form>
	);
};

const formStyles = css({
	margin: 0,
});

/**
 * This allows the input to grow & shrink as the user types in it.
 * See: https://css-tricks.com/auto-growing-inputs-textareas/
 *
 * This can be replaced by the `field-sizing: content;` property when/if it
 * becomes available in browsers.
 * See: https://developer.mozilla.org/en-US/docs/Web/CSS/field-sizing
 */
const inputSizerStyles = css({
	display: 'inline-grid',
	alignItems: 'center',
	'&::after': {
		content: 'attr(data-value)',
		gridArea: '1 / 2',
		visibility: 'hidden',
		whiteSpace: 'pre-wrap',
	},
});

const inputStyles = css({
	background: 'none',
	border: 'none',
	color: 'inherit',
	font: 'inherit',
	gridArea: '1 / 2',
	padding: 0,
	textDecoration: 'inherit',
	whiteSpace: 'inherit',

	'&:focus': {
		outline: 'none',
	},
});

export default memo(TabRenameForm);
